/* ==========================================================================
   procedural-fish.js
   Procedural reef fish — lofted body, real triangle geometry.

   Vendored from the standalone generator: generation code only (the demo's
   scene, UI and coral are dropped). Kept structurally identical so an
   upstream change is a diff rather than a rewrite.

   The body is a lofted surface: a spine with separate dorsal, ventral and
   lateral half-profiles, swept as superelliptical cross-sections. Reef fish
   are laterally compressed, so width and height are independent tables
   rather than one radius — that single split is most of what separates a
   butterflyfish from a mackerel.

   Vertices carry body coordinates (t, yn, zn, part), so every pattern,
   palette and hue change is a recolour of an existing buffer rather than a
   rebuild. Geometry only changes when the form parameters move.
   ========================================================================== */
'use strict';

const TAU = Math.PI * 2;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function sstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }
function mulberry32(a) {
  return function () {
    a |= 0; a = a + 0x6D2B79F5 | 0;
    let t = Math.imul(a ^ a >>> 15, 1 | a);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function makeNoise(seed) {
  const r = mulberry32(seed >>> 0), P = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) P[i] = r();
  function h(x, y, z) {
    let n = (x * 374761393 + y * 668265263 + z * 1442695041) | 0;
    n = (n ^ (n >> 13)) * 1274126177 | 0;
    return P[(n >>> 0) & 1023];
  }
  return function (x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const a = lerp(h(xi, yi, zi), h(xi + 1, yi, zi), u), b = lerp(h(xi, yi + 1, zi), h(xi + 1, yi + 1, zi), u);
    const c = lerp(h(xi, yi, zi + 1), h(xi + 1, yi, zi + 1), u), d = lerp(h(xi, yi + 1, zi + 1), h(xi + 1, yi + 1, zi + 1), u);
    return lerp(lerp(a, b, v), lerp(c, d, v), w);
  };
}
function tableAt(vs, t) {
  const n = vs.length - 1, x = clamp(t, 0, 1) * n, i = Math.min(n - 1, Math.floor(x)), f = x - i;
  return lerp(vs[i], vs[i + 1], f * f * (3 - 2 * f));
}
const mixc = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];

function hueShift(c, deg) {
  if (!deg) return c;
  const a = deg * Math.PI / 180, co = Math.cos(a), si = Math.sin(a);
  const m = [0.213 + co * 0.787 - si * 0.213, 0.715 - co * 0.715 - si * 0.715, 0.072 - co * 0.072 + si * 0.928,
             0.213 - co * 0.213 + si * 0.143, 0.715 + co * 0.285 + si * 0.140, 0.072 - co * 0.072 - si * 0.283,
             0.213 - co * 0.213 - si * 0.787, 0.715 - co * 0.715 + si * 0.715, 0.072 + co * 0.928 + si * 0.072];
  return [clamp(m[0] * c[0] + m[1] * c[1] + m[2] * c[2], 0, 1),
          clamp(m[3] * c[0] + m[4] * c[1] + m[5] * c[2], 0, 1),
          clamp(m[6] * c[0] + m[7] * c[1] + m[8] * c[2], 0, 1)];
}

/* nine-node half-profiles, nose (0) to caudal peduncle (1) */
const PLANS = {
  disc: { name: 'Chaetodon', len: 1.00, n: 2.30,
    top: [0.020, 0.140, 0.260, 0.360, 0.400, 0.360, 0.258, 0.140, 0.045],
    bot: [0.020, 0.120, 0.220, 0.300, 0.330, 0.290, 0.200, 0.100, 0.040],
    wid: [0.015, 0.045, 0.068, 0.080, 0.078, 0.066, 0.048, 0.030, 0.022],
    dorsal: [0.16, 0.94, 0.30], anal: [0.50, 0.92, 0.26], pect: [0.30, 0.36], eye: 0.085 },
  fusiform: { name: 'Pseudanthias', len: 1.10, n: 2.15,
    top: [0.015, 0.085, 0.135, 0.165, 0.170, 0.150, 0.115, 0.070, 0.030],
    bot: [0.015, 0.070, 0.110, 0.130, 0.130, 0.112, 0.085, 0.052, 0.026],
    wid: [0.012, 0.055, 0.085, 0.100, 0.098, 0.084, 0.062, 0.038, 0.020],
    dorsal: [0.24, 0.86, 0.20], anal: [0.58, 0.86, 0.15], pect: [0.30, 0.32], eye: 0.080 },
  elongate: { name: 'Thalassoma', len: 1.42, n: 2.05,
    top: [0.012, 0.062, 0.098, 0.116, 0.120, 0.112, 0.094, 0.062, 0.026],
    bot: [0.012, 0.052, 0.082, 0.096, 0.100, 0.092, 0.076, 0.048, 0.023],
    wid: [0.010, 0.046, 0.070, 0.082, 0.084, 0.076, 0.060, 0.036, 0.017],
    dorsal: [0.20, 0.92, 0.13], anal: [0.55, 0.90, 0.11], pect: [0.28, 0.28], eye: 0.070 },
  boxy: { name: 'Ostracion', len: 0.82, n: 3.70,
    top: [0.030, 0.165, 0.212, 0.224, 0.224, 0.214, 0.170, 0.092, 0.035],
    bot: [0.030, 0.155, 0.202, 0.214, 0.214, 0.204, 0.158, 0.084, 0.032],
    wid: [0.025, 0.105, 0.150, 0.170, 0.165, 0.140, 0.100, 0.055, 0.024],
    dorsal: [0.60, 0.88, 0.15], anal: [0.62, 0.88, 0.14], pect: [0.32, 0.30], eye: 0.090 },
  trigger: { name: 'Balistoides', len: 1.02, n: 2.55,
    top: [0.020, 0.150, 0.250, 0.310, 0.320, 0.290, 0.220, 0.130, 0.048],
    bot: [0.020, 0.110, 0.190, 0.250, 0.262, 0.240, 0.180, 0.105, 0.042],
    wid: [0.016, 0.055, 0.082, 0.095, 0.092, 0.080, 0.058, 0.034, 0.020],
    dorsal: [0.52, 0.90, 0.24], anal: [0.56, 0.90, 0.22], pect: [0.34, 0.32], eye: 0.088 }
};
const PLAN_IDS = Object.keys(PLANS);

const TAILS = ['Forked', 'Lunate', 'Truncate', 'Rounded', 'Lyre'];
function tailLen(kind, u) {
  const a = Math.abs(u);
  if (kind === 0) return 0.46 + 0.54 * Math.pow(a, 1.7);
  if (kind === 1) return 0.26 + 0.74 * Math.pow(a, 2.5);
  if (kind === 2) return 0.94 + 0.06 * a;
  if (kind === 3) return 0.30 + 0.72 * Math.sqrt(Math.max(0, 1 - a * a));
  return 0.34 + 0.66 * Math.pow(a, 3.4);
}

const PATTERNS = ['Plain', 'Bars', 'Stripes', 'Oblique', 'Spots', 'Bicolour', 'Ocellus'];
const PALETTES = [
  { n: 'Butterfly', back: [0.98, 0.78, 0.12], belly: [1.00, 0.95, 0.74], pat: [0.09, 0.10, 0.14], fin: [0.99, 0.82, 0.18], acc: [0.05, 0.06, 0.10] },
  { n: 'Clown',     back: [0.96, 0.40, 0.07], belly: [0.99, 0.62, 0.28], pat: [0.98, 0.98, 0.96], fin: [0.97, 0.48, 0.11], acc: [0.07, 0.06, 0.06] },
  { n: 'Regal',     back: [0.10, 0.32, 0.82], belly: [0.28, 0.54, 0.92], pat: [0.05, 0.07, 0.16], fin: [0.98, 0.84, 0.16], acc: [0.99, 0.88, 0.22] },
  { n: 'Gramma',    back: [0.52, 0.18, 0.80], belly: [0.72, 0.38, 0.92], pat: [0.99, 0.78, 0.14], fin: [0.62, 0.26, 0.86], acc: [0.99, 0.84, 0.20] },
  { n: 'Humbug',    back: [0.95, 0.96, 0.94], belly: [1.00, 1.00, 1.00], pat: [0.07, 0.08, 0.12], fin: [0.90, 0.92, 0.94], acc: [0.05, 0.06, 0.10] },
  { n: 'Anthias',   back: [0.95, 0.28, 0.42], belly: [0.99, 0.60, 0.54], pat: [0.99, 0.80, 0.42], fin: [0.98, 0.42, 0.50], acc: [0.86, 0.90, 0.32] },
  { n: 'Parrot',    back: [0.14, 0.62, 0.52], belly: [0.42, 0.82, 0.66], pat: [0.94, 0.40, 0.56], fin: [0.18, 0.68, 0.60], acc: [0.98, 0.72, 0.28] },
  { n: 'Wrasse',    back: [0.08, 0.50, 0.70], belly: [0.76, 0.88, 0.52], pat: [0.98, 0.60, 0.14], fin: [0.12, 0.56, 0.74], acc: [0.96, 0.90, 0.40] }
];

function Bld() { this.p = []; this.bc = []; this.uv = []; this.i = []; }
Bld.prototype.v = function (p, t, yn, zn, part, u, v) {
  this.p.push(p[0], p[1], p[2]); this.bc.push(t, yn, zn, part); this.uv.push(u, v);
  return this.p.length / 3 - 1;
};
Bld.prototype.tri = function (a, b, c) { this.i.push(a, b, c); };
Bld.prototype.quad = function (a, b, c, d) { this.i.push(a, b, c, a, c, d); };
Bld.prototype.done = function () {
  const P = new Float32Array(this.p), I = new Uint32Array(this.i), N = new Float32Array(P.length);
  for (let k = 0; k < I.length; k += 3) {
    const a = I[k] * 3, b = I[k + 1] * 3, c = I[k + 2] * 3;
    const ux = P[b] - P[a], uy = P[b + 1] - P[a + 1], uz = P[b + 2] - P[a + 2];
    const vx = P[c] - P[a], vy = P[c + 1] - P[a + 1], vz = P[c + 2] - P[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    N[a] += nx; N[a + 1] += ny; N[a + 2] += nz; N[b] += nx; N[b + 1] += ny; N[b + 2] += nz; N[c] += nx; N[c + 1] += ny; N[c + 2] += nz;
  }
  for (let k = 0; k < N.length; k += 3) { const l = Math.hypot(N[k], N[k + 1], N[k + 2]) || 1; N[k] /= l; N[k + 1] /= l; N[k + 2] /= l; }
  return { positions: P, normals: N, body: new Float32Array(this.bc), uvs: new Float32Array(this.uv), indices: I };
};

const P_BODY = 0, P_FIN = 1, P_EYE = 2, P_TAIL = 3;

function buildFish(opts) {
  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const PL = PLANS[opts.plan] || PLANS.disc, rnd = mulberry32(opts.seed >>> 0), nz = makeNoise(opts.seed * 7919 + 11);
  const L = PL.len, NT = opts.NT || 38, NA = opts.NA || 18;
  const dep = opts.depth * (0.92 + rnd() * 0.16), cmp = opts.compress * (0.92 + rnd() * 0.16);
  const fs = opts.fins * (0.9 + rnd() * 0.2);
  const B = new Bld();
  const X = t => L * (1 - t);
  const top = t => tableAt(PL.top, t) * dep, bot = t => tableAt(PL.bot, t) * dep, wid = t => tableAt(PL.wid, t) * cmp;
  const ne = PL.n, ex = 2 / ne;
  const sgnpow = (s, e) => (s < 0 ? -1 : 1) * Math.pow(Math.abs(s), e);

  const rings = [];
  for (let i = 0; i <= NT; i++) {
    const t = i / NT, x = X(t), hT = top(t), hB = bot(t), w = wid(t);
    const ring = [];
    for (let j = 0; j < NA; j++) {
      const a = j / NA * TAU, sa = Math.sin(a), ca = Math.cos(a);
      const h = sa >= 0 ? hT : hB;
      const y = h * sgnpow(sa, ex), z = w * sgnpow(ca, ex);
      const yn = clamp(y / Math.max(hT, hB, 1e-4), -1.6, 1.6);
      ring.push(B.v([x, y, z], t, yn, z / Math.max(w, 1e-4), P_BODY, j / NA, t));
    }
    rings.push(ring);
  }
  for (let i = 0; i < NT; i++) for (let j = 0; j < NA; j++) {
    const j2 = (j + 1) % NA;
    B.quad(rings[i][j], rings[i][j2], rings[i + 1][j2], rings[i + 1][j]);
  }
  const nose = B.v([L + top(0) * 0.5, 0, 0], 0, 0, 0, P_BODY, 0.5, 0);
  for (let j = 0; j < NA; j++) B.tri(rings[0][(j + 1) % NA], rings[0][j], nose);
  const tailC = B.v([0, (top(1) - bot(1)) * 0.5, 0], 1, 0, 0, P_BODY, 0.5, 1);
  for (let j = 0; j < NA; j++) B.tri(rings[NT][j], rings[NT][(j + 1) % NA], tailC);

  function blade(pts, opt) {
    const NU = pts.length - 1, NV = opt.nv || 4, rays = opt.rays || 7;
    const A = [], Bo = [];
    for (let i = 0; i <= NU; i++) {
      const uu = i / NU, P = pts[i];
      const ra = [], rb = [];
      for (let j = 0; j <= NV; j++) {
        const vv = j / NV;
        const scallop = 1 - (opt.scallop || 0.10) * Math.abs(Math.sin(uu * rays * Math.PI));
        const ridge = Math.sin(uu * rays * TAU) * (opt.ray || 0.010) * vv * fs;
        const curl = (opt.curl || 0) * vv * vv;
        const l = P.L * vv * scallop;
        const p = [P.p[0] + P.v[0] * l + P.n[0] * (ridge + curl) + (opt.sweep || 0) * vv * P.L * P.sw[0],
                   P.p[1] + P.v[1] * l + P.n[1] * (ridge + curl) + (opt.sweep || 0) * vv * P.L * P.sw[1],
                   P.p[2] + P.v[2] * l + P.n[2] * (ridge + curl) + (opt.sweep || 0) * vv * P.L * P.sw[2]];
        const th = (opt.th || 0.006) * (1 - 0.75 * vv) * fs;
        ra.push(B.v([p[0] + P.n[0] * th, p[1] + P.n[1] * th, p[2] + P.n[2] * th], P.t, P.yn, 0.55, opt.part || P_FIN, uu, vv));
        rb.push(B.v([p[0] - P.n[0] * th, p[1] - P.n[1] * th, p[2] - P.n[2] * th], P.t, P.yn, -0.55, opt.part || P_FIN, uu, vv));
      }
      A.push(ra); Bo.push(rb);
    }
    for (let i = 0; i < NU; i++) for (let j = 0; j < NV; j++) {
      B.quad(A[i][j], A[i][j + 1], A[i + 1][j + 1], A[i + 1][j]);
      B.quad(Bo[i][j + 1], Bo[i][j], Bo[i + 1][j], Bo[i + 1][j + 1]);
    }
    for (let i = 0; i < NU; i++) B.quad(A[i][NV], Bo[i][NV], Bo[i + 1][NV], A[i + 1][NV]);
    for (let j = 0; j < NV; j++) {
      B.quad(A[0][j + 1], Bo[0][j + 1], Bo[0][j], A[0][j]);
      B.quad(Bo[NU][j + 1], A[NU][j + 1], A[NU][j], Bo[NU][j]);
    }
  }

  function ridgeFin(spec, up) {
    const t0f = spec[0], t1f = spec[1], hgt = spec[2], N = 32, pts = [];
    for (let i = 0; i <= N; i++) {
      const uu = i / N, t = lerp(t0f, t1f, uu);
      const prof = Math.sin(Math.pow(uu, 0.72) * Math.PI);
      const spine = up ? top(t) : -bot(t);
      pts.push({ p: [X(t), spine * 0.94, 0], v: [0, up ? 1 : -1, 0], n: [0, 0, 1], sw: [-1, 0, 0],
                 L: hgt * L * fs * (0.30 + 0.70 * prof) * (0.85 + 0.3 * nz(uu * 3, 7, 0)),
                 t: t, yn: up ? 1.25 : -1.25 });
    }
    blade(pts, { nv: 5, rays: up ? 7 : 6, ray: 0.007, scallop: 0.14, th: 0.005, sweep: 0.10 });
  }
  ridgeFin(PL.dorsal, true);
  ridgeFin(PL.anal, false);

  (function () {
    const N = 34, pts = [], hT = top(1), hB = bot(1), cl = L * 0.34 * fs;
    for (let i = 0; i <= N; i++) {
      const uu = i / N, u = uu * 2 - 1;
      const y = u >= 0 ? hT * u * 1.7 : -hB * (-u) * 1.7;
      pts.push({ p: [0, y, 0], v: [-1, 0, 0], n: [0, 0, 1], sw: [0, 0, 0],
                 L: cl * tailLen(opts.tail, u), t: 1.04, yn: clamp(u * 1.4, -1.4, 1.4) });
    }
    blade(pts, { nv: 7, rays: 8, ray: 0.006, scallop: 0.07, th: 0.005, part: P_TAIL });
  })();

  (function () {
    const pt = PL.pect[0], plen = PL.pect[1], N = 16;
    for (const s of [1, -1]) {
      const pts = [];
      for (let i = 0; i <= N; i++) {
        const uu = i / N, t = pt + uu * 0.07, w = wid(t), yb = (top(t) - bot(t)) * 0.5 - bot(t) * 0.18;
        pts.push({ p: [X(t), yb + (0.5 - uu) * 0.05 * L, s * w * 0.92],
                   v: [-0.55, -0.30, s * 0.78], n: [0, 1, 0], sw: [-1, 0, 0],
                   L: plen * L * fs * Math.sin(Math.pow(uu, 0.6) * Math.PI) * 0.95 + 0.02 * L,
                   t: t, yn: 0.1 });
      }
      blade(pts, { nv: 5, rays: 4, ray: 0.006, scallop: 0.16, th: 0.004, curl: 0.02 * L * s });
    }
  })();

  (function () {
    const N = 12;
    for (const s of [1, -1]) {
      const pts = [];
      for (let i = 0; i <= N; i++) {
        const uu = i / N, t = 0.40 + uu * 0.05;
        pts.push({ p: [X(t), -bot(t) * 0.86, s * wid(t) * 0.42], v: [-0.35, -0.90, s * 0.25], n: [0, 0, 1], sw: [0, 0, 0],
                   L: 0.14 * L * fs * Math.sin(Math.pow(uu, 0.7) * Math.PI) + 0.01 * L, t: t, yn: -1.2 });
      }
      blade(pts, { nv: 4, rays: 3, ray: 0.005, scallop: 0.18, th: 0.004 });
    }
  })();

  (function () {
    const et = PL.eye, r = Math.max(top(et), bot(et)) * 0.30, w = wid(et);
    for (const s of [1, -1]) {
      const cx = X(et), cyy = top(et) * 0.34, cz = s * (w * 0.80);
      const NU2 = 10, NV2 = 12, g = [];
      for (let i = 0; i <= NU2; i++) {
        const ph = i / NU2 * Math.PI, ro = [];
        for (let j = 0; j < NV2; j++) {
          const th = j / NV2 * TAU;
          const p = [cx + r * Math.sin(ph) * Math.cos(th) * 1.15, cyy + r * Math.cos(ph), cz + s * r * Math.sin(ph) * Math.sin(th) * 0.55];
          const d = Math.hypot(Math.sin(ph) * Math.cos(th), Math.cos(ph));
          ro.push(B.v(p, et, d, s, P_EYE, j / NV2, i / NU2));
        }
        g.push(ro);
      }
      for (let i = 0; i < NU2; i++) for (let j = 0; j < NV2; j++) {
        const j2 = (j + 1) % NV2;
        if (s > 0) B.quad(g[i][j], g[i][j2], g[i + 1][j2], g[i + 1][j]);
        else B.quad(g[i][j2], g[i][j], g[i + 1][j], g[i + 1][j2]);
      }
    }
  })();

  const m = B.done();
  const cx = L * 0.42;
  for (let i = 0; i < m.positions.length; i += 3) m.positions[i] -= cx;
  m.noseX = L - cx; m.len = L; m.plan = opts.plan; m.seed = opts.seed;
  m.tris = m.indices.length / 3;
  m.colors = new Float32Array(m.positions.length);
  m.ms = (typeof performance !== 'undefined' ? performance : Date).now() - t0;
  return m;
}

/* livery — a pure recolour from the stored body coordinates */
function paint(m, o) {
  const nz = makeNoise(m.seed * 104729 + 5);
  const P = PALETTES[o.palette % PALETTES.length];
  const hs = o.hue || 0;
  const back = hueShift(P.back, hs), belly = hueShift(P.belly, hs), pat = hueShift(P.pat, hs);
  const fin = hueShift(P.fin, hs), acc = hueShift(P.acc, hs);
  const K = o.contrast, N = o.patn, kind = o.pattern;
  const bc = m.body, C = m.colors;

  for (let i = 0, v = 0; i < bc.length; i += 4, v += 3) {
    const t = bc[i], yn = bc[i + 1], zn = bc[i + 2], part = bc[i + 3];
    if (part === P_EYE) {
      const d = yn;
      let c = mixc([0.03, 0.035, 0.05], acc, sstep(0.34, 0.62, d));
      c = mixc(c, [0.92, 0.96, 1.0], sstep(0.88, 1.0, d) * 0.5);
      C[v] = c[0]; C[v + 1] = c[1]; C[v + 2] = c[2];
      continue;
    }
    let c = mixc(belly, back, sstep(-0.55, 0.35, yn));
    let w = 0;
    const wob = (nz(t * 3.1, yn * 1.6, 4) - 0.5) * 0.35;
    if (kind === 1) w = sstep(0.12, 0.44, Math.sin((t * N + wob) * Math.PI));
    else if (kind === 2) w = sstep(0.12, 0.44, Math.sin((yn * N * 0.55 + t * 0.6 + wob) * Math.PI));
    else if (kind === 3) w = sstep(0.12, 0.44, Math.sin(((t * 1.3 + yn * 0.75) * N + wob) * Math.PI));
    else if (kind === 4) { const s = nz(t * N * 1.9, yn * N * 1.1, zn * 0.5 + 9); w = sstep(0.60, 0.76, s); }
    else if (kind === 5) w = sstep(0.46, 0.62, t + wob * 0.10);
    else if (kind === 6) {
      const d = Math.hypot((t - 0.82) * 3.0, (yn - 0.30) * 1.5);
      w = sstep(0.42, 0.30, d);
      c = mixc(c, acc, sstep(0.62, 0.46, d) * 0.85);
    }
    c = mixc(c, pat, w * K);
    const mask = sstep(0.16, 0.09, Math.abs(t - 0.085 + yn * 0.035));
    c = mixc(c, pat, mask * K * 0.85);
    c = mixc(c, mixc(c, [0, 0, 0], 0.35), sstep(0.030, 0.004, Math.abs(t - 0.235)));
    c = mixc(c, acc, sstep(0.055, 0.015, Math.abs(t - 0.93)) * K * 0.55);
    if (part === P_FIN || part === P_TAIL) c = mixc(c, fin, 0.55);
    const g = 1 + (nz(t * 46, yn * 18, zn * 4) - 0.5) * 0.10;
    C[v] = clamp(c[0] * g, 0, 1); C[v + 1] = clamp(c[1] * g, 0, 1); C[v + 2] = clamp(c[2] * g, 0, 1);
  }
}

export { buildFish, paint, PLANS, PLAN_IDS, PALETTES, PATTERNS, TAILS };
export default { buildFish, paint, PLANS, PLAN_IDS, PALETTES, PATTERNS, TAILS };
